import { FC, RefObject, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Transition, TransitionStatus } from 'react-transition-group'

import Close from '@material-ui/icons/Close'
import { IconButton } from '@material-ui/core'

import { RootState } from 'src/Store'
import { profileActions } from 'src/actions/ProfileActions'
import { ProfileView } from 'src/reducers/ProfileReducer'
import { ProfileObject } from 'src/dataSources/geo/CrossSection'
import { generalActions } from 'src/actions/GeneralActions'

import { CO2LevelsOrg } from '../co2levelsorg/CO2LevelsOrg'
import { ProfileInfo } from '../profileinfo/ProfileInfo'

import { bottomBarStyle } from './BottomBar.style'
import { Graph } from 'src/dataSources/graphs/GraphRegistry'
import { Rnd } from 'react-rnd'
import { sidebarAnimationDuration } from 'src/Constants'

export interface BottomBarProps {
  open: boolean
}

const defaultProps: BottomBarProps = {
  open: false
}

/**
 * Hook to get div dimensions when resized.
 * @param ref Reference to div element.
 * @returns Div dimensions.
 */
const useContainerDimensions = (ref: RefObject<HTMLDivElement>): {} => {
  const dispatch = useDispatch()

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  const getDimensions = (): { width: number, height: number } | undefined => {
    if (ref === undefined || ref.current === undefined) {
      return undefined
    }
    const width = ref.current?.offsetWidth
    const height = ref.current?.offsetHeight
    return {
      width: width !== undefined ? width : 0,
      height: height !== undefined ? height : 0
    }
  }

  useEffect(() => {
    const handleResize = (): void => {
      const dimensions = getDimensions()
      if (dimensions !== undefined && dimensions.width !== 0 && dimensions.height !== 0) {
        setDimensions(dimensions)
        dispatch(generalActions.setBottomBarWidth(dimensions.width))
        dispatch(generalActions.setBottomBarHeight(dimensions.height))
      }
    }

    if (ref.current != null) {
      handleResize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
    // eslint-disable-next-line
  }, [ref])
  return dimensions
}

/**
 * BottomBar React component.
 * @param {object} props React properties object.
 * The following properties are implemented:
 * open {boolean}: If true the component will be open initially.
 * @returns React view component.
 */
export const BottomBar: FC<BottomBarProps> = (props: BottomBarProps) => {
  const dispatch = useDispatch()
  const classes = bottomBarStyle()
  const currentView = useSelector((state: RootState): ProfileView => state.profileState.currentView)
  const selectedGraph = useSelector((state: RootState): Graph | undefined => state.generalState.selectedGraph)
  const selectedProfile = useSelector((state: RootState): ProfileObject | undefined => state.profileState.selectedProfile)
  const bottomBarHeight = useSelector((state: RootState): number => state.generalState.bottomBarHeight)
  const dragging = useSelector((state: RootState): Boolean => state.generalState.bottomBarDragging)

  // Styles for sidebar in different transitions
  const bottomBarTransitionStyle = {
    transition: `height ${dragging === true ? 0 : sidebarAnimationDuration} ms`
  }
  const bottomBarTransitionStyles = {
    entering: { height: 0 },
    entered: { height: `${bottomBarHeight}px` },
    exiting: { height: `${bottomBarHeight}px` },
    exited: { height: 0 },
    unmounted: { height: 0 }
  }

  // Handle reload on resize
  const ref = useRef<HTMLDivElement>(null)
  useContainerDimensions(ref)

  /**
   * Set to empty view when close button is clicked.
   */
  const closeBarClicked = (): void => {
    dispatch(profileActions.clearState())
    dispatch(generalActions.setSelectedGraph(undefined))
  }
  return (
    <>
      <Transition in={currentView !== ProfileView.Empty || selectedGraph !== undefined} timeout={0}>
        {(state: TransitionStatus) => (
          <>
            {(currentView !== ProfileView.Empty || selectedGraph !== undefined) && (
              <div
                style={{ ...bottomBarTransitionStyle, ...bottomBarTransitionStyles[state] }}
                className={classes.root} ref={ref}
              >
                <div className={classes.closeButton}>
                  <IconButton size='medium' className={classes.closeButtonIcon} onClick={closeBarClicked}>
                    <Close />
                  </IconButton>
                </div>
                <Rnd
                  disableDragging
                  enableResizing={{ top: true }}
                  position={{ x: 0, y: 0 }}
                  size={{ width: '100%', height: bottomBarHeight }}
                  minHeight={200}
                  maxHeight={700}
                  onResizeStart={() => {
                    dispatch(generalActions.setBottomBarDragging(true))
                  }}
                  onResize={(e, direction, ref, delta, position) => {
                    dispatch(generalActions.setBottomBarHeight(parseInt(ref.style.height)))
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    dispatch(generalActions.setBottomBarHeight(parseInt(ref.style.height)))
                    dispatch(generalActions.setBottomBarDragging(false))
                  }}
                >
                  <div>
                    {dragging === false && (
                      <>
                        {currentView !== ProfileView.Empty && (
                          <ProfileInfo selectedProfile={selectedProfile} />
                        )}
                        {selectedGraph === Graph.CO2Levels && (
                          <CO2LevelsOrg />
                        )}
                      </>
                    )}
                  </div>
                </Rnd>
              </div>
            )}
          </>
        )}
      </Transition>
    </>
  )
}

BottomBar.defaultProps = defaultProps

export default BottomBar
